/**
 * BuddyXin — 赛博像素伴侣「信（XIN）」
 * 32×32 点阵整数放大 Canvas · 4 帧待机循环 · 瞳孔随鼠标 · 摸头/发怒/瞌睡/持盾/抱箱
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { audioEngine } from "@/lib/audioEngine";
import {
  rollBuddy,
  STAT_META,
  SPECIES_LABEL,
  ACCESSORY_LABEL,
  ACCESSORY_META,
  type BuddyAnim,
  type BuddyTraits,
  type Rarity,
} from "@/lib/buddyEngine";
import {
  BUDDY_IDENTITY_META,
  getBuddyIdentity,
  subscribeBuddyIdentity,
  type BuddyIdentity,
} from "@/lib/buddyVoice";

export type { BuddyAnim };

export interface BuddyXinProps {
  seedInput: string;
  /** 由对话状态机驱动的姿势 */
  anim: BuddyAnim;
  onPoke: () => void;
  className?: string;
}

const GRID = 32;
const SCALE = 6; // 整数放大，保证像素边缘锐利

/** 4 帧呼吸沉浮（像素位移，逐帧整数） */
const BOB = [0, -1, -2, -1] as const;

export type Pt = { x: number; y: number };

export interface Scene {
  t: number;
  mouse: Pt | null;
  mode: BuddyAnim;
  /** 当前姿态已持续毫秒 */
  elapsed: number;
}

type Ctx = CanvasRenderingContext2D;

/** 整数像素矩形，杜绝半像素模糊 */
function px(ctx: Ctx, x: number, y: number, w = 1, h = 1, color?: string) {
  if (color) ctx.fillStyle = color;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
}

function drawEyes(
  ctx: Ctx,
  eyes:
    | "open"
    | "blink"
    | "happy"
    | "angry"
    | "closed"
    | "wide"     // 警觉/恐惧：圆睁大瞳
    | "narrow"   // 专注：眯缝锐目
    | "half"     // 疲惫：半垂眼睑
    | "sad"      // 悲伤：垂目含泪
    | "spiral",  // 恍惚：螺旋瞳
  eyeL: number,
  eyeR: number,
  eyeY: number,
  mouse: Pt | null,
) {
  const dark = "#0f111a";
  if (eyes === "wide") {
    // 圆睁：白多瞳小（惊/警）
    px(ctx, eyeL, eyeY - 1, 4, 5, dark);
    px(ctx, eyeR, eyeY - 1, 4, 5, dark);
    px(ctx, eyeL + 1, eyeY, 2, 2, "#f1f5f9");
    px(ctx, eyeR + 1, eyeY, 2, 2, "#f1f5f9");
    px(ctx, eyeL + 1, eyeY + 3, 1, 1, "#f1f5f9");
    px(ctx, eyeR + 1, eyeY + 3, 1, 1, "#f1f5f9");
    return;
  }
  if (eyes === "narrow") {
    // 眯缝：水平锐线 + 压低眉
    px(ctx, eyeL, eyeY + 1, 4, 1, dark);
    px(ctx, eyeR, eyeY + 1, 4, 1, dark);
    px(ctx, eyeL + 1, eyeY + 1, 2, 1, "#f1f5f9");
    px(ctx, eyeR + 1, eyeY + 1, 2, 1, "#f1f5f9");
    px(ctx, eyeL, eyeY - 1, 4, 1, dark);
    px(ctx, eyeR, eyeY - 1, 4, 1, dark);
    return;
  }
  if (eyes === "half") {
    // 半垂：下弧线 + 上睑压半
    px(ctx, eyeL, eyeY, 4, 2, dark);
    px(ctx, eyeR, eyeY, 4, 2, dark);
    px(ctx, eyeL + 1, eyeY + 2, 2, 1, "#94a3b8");
    px(ctx, eyeR + 1, eyeY + 2, 2, 1, "#94a3b8");
    return;
  }
  if (eyes === "sad") {
    // 垂目：八字眉 + 泪光点
    px(ctx, eyeL, eyeY + 1, 3, 2, dark);
    px(ctx, eyeR + 1, eyeY + 1, 3, 2, dark);
    px(ctx, eyeL, eyeY - 1, 3, 1, dark);
    px(ctx, eyeR + 1, eyeY - 1, 3, 1, dark);
    px(ctx, eyeL + 1, eyeY + 3, 1, 1, "#38bdf8");
    px(ctx, eyeR + 2, eyeY + 3, 1, 1, "#38bdf8");
    return;
  }
  if (eyes === "spiral") {
    // 螺旋瞳：同心矩形环（像素螺旋近似）
    px(ctx, eyeL, eyeY, 4, 4, dark);
    px(ctx, eyeR, eyeY, 4, 4, dark);
    px(ctx, eyeL + 1, eyeY + 1, 2, 2, "#8b5cf6");
    px(ctx, eyeR + 1, eyeY + 1, 2, 2, "#8b5cf6");
    px(ctx, eyeL + 1, eyeY + 1, 1, 1, "#00f0ff");
    px(ctx, eyeR + 2, eyeY + 2, 1, 1, "#00f0ff");
    return;
  }
  if (eyes === "blink") {
    px(ctx, eyeL, eyeY + 2, 4, 1, dark);
    px(ctx, eyeR, eyeY + 2, 4, 1, dark);
    return;
  }
  if (eyes === "happy") {
    // ^ ^
    px(ctx, eyeL + 1, eyeY, 1, 1, dark);
    px(ctx, eyeL, eyeY + 1, 1, 1, dark);
    px(ctx, eyeL + 2, eyeY + 1, 1, 1, dark);
    px(ctx, eyeR + 1, eyeY, 1, 1, dark);
    px(ctx, eyeR, eyeY + 1, 1, 1, dark);
    px(ctx, eyeR + 2, eyeY + 1, 1, 1, dark);
    return;
  }
  if (eyes === "angry") {
    // \ /
    px(ctx, eyeL, eyeY, 1, 1, dark);
    px(ctx, eyeL + 1, eyeY + 1, 1, 1, dark);
    px(ctx, eyeL + 2, eyeY + 2, 1, 1, dark);
    px(ctx, eyeR + 2, eyeY, 1, 1, dark);
    px(ctx, eyeR + 1, eyeY + 1, 1, 1, dark);
    px(ctx, eyeR, eyeY + 2, 1, 1, dark);
    return;
  }
  if (eyes === "closed") {
    // 瞌睡弧线 ∪
    px(ctx, eyeL, eyeY + 1, 1, 1, dark);
    px(ctx, eyeL + 1, eyeY + 2, 2, 1, dark);
    px(ctx, eyeL + 3, eyeY + 1, 1, 1, dark);
    px(ctx, eyeR, eyeY + 1, 1, 1, dark);
    px(ctx, eyeR + 1, eyeY + 2, 2, 1, dark);
    px(ctx, eyeR + 3, eyeY + 1, 1, 1, dark);
    return;
  }
  // open：暗底亮瞳，瞳孔追踪鼠标（±1px）
  const pupil = { x: 0, y: 0 };
  if (mouse) {
    const dx = mouse.x - GRID / 2;
    const dy = mouse.y - (eyeY + 2);
    pupil.x = Math.max(-1, Math.min(1, Math.round(dx / 6)));
    pupil.y = Math.max(-1, Math.min(1, Math.round(dy / 6)));
  }
  px(ctx, eyeL, eyeY, 4, 4, dark);
  px(ctx, eyeR, eyeY, 4, 4, dark);
  px(ctx, eyeL + 1 + pupil.x, eyeY + 1 + pupil.y, 2, 2, "#f1f5f9");
  px(ctx, eyeR + 1 + pupil.x, eyeY + 1 + pupil.y, 2, 2, "#f1f5f9");
}

/** 单帧角色绘制（纯函数，可离屏复用） */
export function drawBuddy(ctx: Ctx, traits: BuddyTraits, scene: Scene) {
  const { t, mouse, mode, elapsed } = scene;
  const { bodyColor, accentColor, species, accessories, shiny } = traits;

  // ── 4 帧待机时钟（待机呼吸 / 瞌睡慢呼吸） ──
  const frame4 = Math.floor(t / 280) % 4;
  const sleep4 = Math.floor(t / 560) % 4;

  // ── 姿态解算 ──
  let offX = 0;
  let offY = 0;
  let squash = 0; // 顶部下移/压扁像素（落地、摸头下压）
  let eyes:
    | "open"
    | "blink"
    | "happy"
    | "angry"
    | "closed"
    | "wide"
    | "narrow"
    | "half"
    | "sad"
    | "spiral" = "open";

  if (mode === "idle") {
    offY = BOB[frame4];
  } else if (mode === "sleep") {
    offY = BOB[sleep4];
    eyes = "closed";
  } else if (mode === "drop") {
    const p = Math.min(1, elapsed / 800);
    if (p < 1) {
      offY = Math.round(-26 + 26 * p * p); // 从天而降·加速下落
    } else {
      const rebound = Math.min(1, (elapsed - 800) / 320);
      squash = Math.round(2 * (1 - rebound)); // 落地回弹压扁
      offY = BOB[frame4];
    }
  } else if (mode === "pet") {
    offY = BOB[frame4];
    squash = elapsed < 280 ? 2 : elapsed < 460 ? 1 : 0; // 拍头下压
    eyes = "happy";
  } else if (mode === "angry") {
    offX = Math.floor(t / 110) % 2 === 0 ? -1 : 1; // 高频左右抖动
    offY = BOB[frame4];
    eyes = "angry";
  } else if (mode === "hold-shield") {
    offX = 1;
    offY = BOB[frame4];
  } else if (mode === "hold-chest") {
    offY = BOB[frame4];
  } else if (mode === "calm") {
    // 平静：匀速呼吸浮动（慢于 idle 待机钟）
    offY = BOB[sleep4];
    eyes = "open";
  } else if (mode === "joy") {
    // 喜悦：轻快上跳（正弦弹跳）+ 笑眼
    offY = -Math.abs(Math.round(Math.sin(t / 180) * 3));
    eyes = "happy";
  } else if (mode === "focus") {
    // 专注：微沉前倾 + 眯缝锐目
    offY = 1;
    eyes = "narrow";
  } else if (mode === "alert") {
    // 警觉：蹑足上探（高位悬停）+ 圆睁
    offY = BOB[frame4] - 2;
    eyes = "wide";
  } else if (mode === "rage") {
    // 愤怒：高频狂抖（双倍 angry 频率）+ 怒目
    offX = Math.floor(t / 70) % 2 === 0 ? -2 : 2;
    offY = BOB[frame4];
    eyes = "angry";
  } else if (mode === "fear") {
    // 恐惧：高频战栗（x/y 双轴微颤）+ 惊目
    offX = Math.floor(t / 55) % 2 === 0 ? -1 : 1;
    offY = BOB[frame4] + (Math.floor(t / 73) % 2);
    eyes = "wide";
  } else if (mode === "fatigue") {
    // 疲惫：塌肩慢浮（超慢钟）+ 半垂眼
    offY = 1 + BOB[sleep4];
    eyes = "half";
  } else if (mode === "sorrow") {
    // 悲伤：塌陷下沉 + 垂泪
    offY = 2 + BOB[sleep4];
    eyes = "sad";
  } else if (mode === "trance") {
    // 恍惚：慢速悬浮上飘（缓正弦）+ 螺旋瞳
    offY = Math.round(Math.sin(t / 900) * 2) - 1;
    eyes = "spiral";
  }

  if (eyes === "open" && t % 2600 < 150) eyes = "blink"; // 眼眸眨眼

  // ── 身体几何（32×32 点阵，主体 16×13） ──
  const bx = 8;
  const bw = 16;
  const by = 11 + squash;
  const bh = 13 - squash;

  ctx.save();
  ctx.translate(offX, offY);

  // 脚（不随压扁移动）
  if (species === "ghost") {
    // 幽灵底部波浪脚
    for (let i = 0; i < 4; i++) {
      px(ctx, bx + i * 4, 24, 3, 2 + (i % 2), bodyColor);
    }
  } else {
    px(ctx, bx + 2, 24, 4, 3, bodyColor);
    px(ctx, bx + bw - 6, 24, 4, 3, bodyColor);
    px(ctx, bx + 2, 26, 4, 1, "rgba(15,17,26,0.35)");
    px(ctx, bx + bw - 6, 26, 4, 1, "rgba(15,17,26,0.35)");
  }

  // 躯干
  if (species === "ghost") {
    px(ctx, bx + 2, by, bw - 4, bh, bodyColor); // 顶部收圆
    px(ctx, bx, by + 2, bw, bh - 2, bodyColor);
    for (let i = 0; i < 4; i++) {
      px(ctx, bx + i * 4, by + bh, 3, 2 + (i % 2), bodyColor);
    }
  } else {
    px(ctx, bx, by, bw, bh, bodyColor);
    // 内阴影 + 高光
    px(ctx, bx + bw - 3, by, 3, bh, "rgba(15,17,26,0.22)");
    px(ctx, bx, by + bh - 2, bw, 2, "rgba(15,17,26,0.22)");
    px(ctx, bx + 1, by + 1, 3, 2, "rgba(255,255,255,0.35)");
  }

  // 物种形态差异
  const cx = bx + bw / 2;
  const hasAntenna = accessories.includes("antenna");
  if (species === "robot") {
    // 侧栓 + 胸口面板
    px(ctx, bx - 2, by + 4, 2, 3, "#94a3b8");
    px(ctx, bx + bw, by + 4, 2, 3, "#94a3b8");
    px(ctx, bx + 5, by + 8, 6, 3, "rgba(15,17,26,0.30)");
    px(ctx, bx + 6, by + 9, 1, 1, accentColor);
    if (!hasAntenna) {
      // 状态天线·微闪
      px(ctx, cx - 1, by - 3, 2, 3, accentColor);
      px(ctx, cx - 1, by - 4, 2, 1, frame4 % 2 === 0 ? "#f1f5f9" : accentColor);
    }
  } else if (species === "neko") {
    // 猫耳 + 胡须 + 尾巴
    px(ctx, bx + 2, by - 3, 3, 3, bodyColor);
    px(ctx, bx + bw - 5, by - 3, 3, 3, bodyColor);
    px(ctx, bx + 3, by - 2, 1, 1, accentColor);
    px(ctx, bx + bw - 4, by - 2, 1, 1, accentColor);
    px(ctx, bx - 3, by + 5, 2, 1, accentColor);
    px(ctx, bx - 3, by + 7, 2, 1, accentColor);
    px(ctx, bx + bw + 1, by + 5, 2, 1, accentColor);
    px(ctx, bx + bw + 1, by + 7, 2, 1, accentColor);
    px(ctx, bx + bw, by + 8, 2, 2, bodyColor);
    px(ctx, bx + bw + 2, by + 5, 2, 4, bodyColor);
    px(ctx, bx + bw + 2, by + 5, 1, 1, accentColor);
  }

  // ── 眼睛 ──
  const eyeY = by + 4;
  const eyeL = bx + 3;
  const eyeR = bx + bw - 7;
  drawEyes(ctx, eyes, eyeL, eyeR, eyeY, mouse);

  // ── 摸头脸红 ──
  if (mode === "pet") {
    px(ctx, bx + 1, by + 8, 3, 2, "rgba(255,122,217,0.75)");
    px(ctx, bx + bw - 4, by + 8, 3, 2, "rgba(255,122,217,0.75)");
  }

  // ── 配饰（稀有度梯队） ──
  for (const a of accessories) {
    if (a === "antenna") {
      // 头顶天线（精良）· 微闪
      px(ctx, cx - 1, by - 6, 2, 6, accentColor);
      px(ctx, cx - 2, by - 8, 4, 2, accentColor);
      px(ctx, cx - 2, by - 8, 2, 1, frame4 % 2 === 0 ? "#f1f5f9" : accentColor);
    } else if (a === "goggles") {
      // 护目镜：镜框 + 镜桥，瞳孔仍可见
      px(ctx, eyeL - 1, eyeY - 1, 6, 1, "#202840");
      px(ctx, eyeL - 1, eyeY + 4, 6, 1, "#202840");
      px(ctx, eyeL - 1, eyeY, 1, 4, "#202840");
      px(ctx, eyeL + 4, eyeY, 1, 4, "#202840");
      px(ctx, eyeR - 1, eyeY - 1, 6, 1, "#202840");
      px(ctx, eyeR - 1, eyeY + 4, 6, 1, "#202840");
      px(ctx, eyeR - 1, eyeY, 1, 4, "#202840");
      px(ctx, eyeR + 4, eyeY, 1, 4, "#202840");
      px(ctx, eyeL + 5, eyeY + 1, 3, 1, "#202840");
      px(ctx, eyeL - 1, eyeY - 1, 6, 1, accentColor);
    } else if (a === "halo") {
      // 金色法环（稀有）
      const gold = frame4 % 2 === 0 ? "#ffe066" : "#fbbf24";
      px(ctx, bx + 4, by - 9, 8, 2, gold);
      px(ctx, bx + 3, by - 8, 1, 1, gold);
      px(ctx, bx + 12, by - 8, 1, 1, gold);
    } else if (a === "duck-hat") {
      // 小鸭子帽（传说）
      px(ctx, bx + 3, by - 5, 10, 5, "#ffe066");
      px(ctx, bx + 3, by - 6, 8, 1, "#ffe066");
      px(ctx, bx + 1, by - 3, 3, 2, "#ff9f5a"); // 鸭嘴
      px(ctx, bx + 6, by - 4, 1, 1, "#0f111a"); // 鸭眼
      px(ctx, bx + 7, by - 5, 1, 1, "#fbbf24");
    }
  }

  // ── 持盾姿势：对讲机 + 警戒盾 ──
  if (mode === "hold-shield") {
    // 右手掏对讲机
    px(ctx, bx + bw - 1, by + 3, 2, 2, bodyColor);
    px(ctx, bx + bw + 1, by + 2, 3, 5, "#202840");
    px(ctx, bx + bw + 2, by - 1, 1, 3, "#94a3b8");
    px(ctx, bx + bw + 2, by + 3, 1, 1, frame4 % 2 === 0 ? "#00f0ff" : "#0f111a");
    px(ctx, bx + bw + 2, by + 5, 1, 1, "#94a3b8");
    // 左手举警戒盾（半透明青光）
    px(ctx, 2, 12, 10, 15, "rgba(0,240,255,0.20)");
    px(ctx, 2, 12, 10, 1, "#00f0ff");
    px(ctx, 2, 26, 10, 1, "#00f0ff");
    px(ctx, 2, 12, 1, 15, "#00f0ff");
    px(ctx, 11, 12, 1, 15, "#00f0ff");
    px(ctx, 6, 17, 2, 6, "rgba(0,240,255,0.55)");
    px(ctx, 4, 19, 6, 2, "rgba(0,240,255,0.55)");
  }

  // ── 抱箱姿势：高举黄金宝箱 ──
  if (mode === "hold-chest") {
    // 双臂上举
    px(ctx, bx + 1, by - 2, 2, 3, bodyColor);
    px(ctx, bx + bw - 3, by - 2, 2, 3, bodyColor);
    // 箱体
    const cy = 4 + (frame4 % 2);
    px(ctx, 11, cy, 10, 5, "#ffe066");
    px(ctx, 11, cy - 2, 10, 2, "#ff9f5a"); // 箱盖
    px(ctx, 12, cy, 1, 5, "#d4a017");
    px(ctx, 19, cy, 1, 5, "#d4a017");
    px(ctx, 15, cy + 1, 2, 3, "#0f111a"); // 锁
    px(ctx, 16, cy + 2, 1, 1, "#ffe066");
    // 闪光
    px(ctx, 9, cy - 1, 1, 1, frame4 % 2 === 0 ? "#fbbf24" : "#ffe066");
    px(ctx, 22, cy + 1, 1, 1, frame4 % 2 === 0 ? "#ffe066" : "#fbbf24");
    px(ctx, 21, cy - 2, 1, 1, "#fbbf24");
  }

  // ── Shiny：高频像素粒子描边（青→紫→粉→金，禁荧光绿） ──
  if (shiny) {
    const colors = ["#00f0ff", "#8b5cf6", "#ff7ad9", "#fbbf24"];
    const phase = Math.floor(t / 90);
    for (let i = 0; i < 14; i++) {
      const ang = phase * 0.45 + (i * Math.PI * 2) / 14;
      const r = 13 + ((i + phase) % 2);
      const x = GRID / 2 + Math.cos(ang) * r * 0.95;
      const y = 17 + Math.sin(ang) * r * 0.95;
      px(ctx, x, y, 1, 1, colors[(i + phase) % colors.length]);
    }
  }

  ctx.restore();
}

/** 摸头：漂浮粉色像素爱心 */
function drawHearts(ctx: Ctx, t: number) {
  for (let i = 0; i < 5; i++) {
    const life = (t / 500 + i * 0.2) % 1;
    const x = 8 + i * 5 + Math.sin(life * Math.PI * 2) * 2;
    const y = 22 - life * 16;
    ctx.globalAlpha = 1 - life;
    px(ctx, x, y, 2, 2, "#ff7ad9");
    px(ctx, x + 2, y, 2, 2, "#ff7ad9");
    px(ctx, x - 1, y + 1, 6, 2, "#ff7ad9");
    px(ctx, x, y + 3, 4, 1, "#ff7ad9");
    px(ctx, x + 1, y + 4, 2, 1, "#ff7ad9");
    ctx.globalAlpha = 1;
  }
}

/** 打瞌睡：漂浮 Zzz */
function drawZzz(ctx: Ctx, t: number) {
  const phases = [0, 0.33, 0.66];
  phases.forEach((p, i) => {
    const life = (t / 900 + p) % 1;
    ctx.globalAlpha = 1 - life;
    const x = 22 + i * 3;
    const y = 8 - life * 8;
    const s = 1 + i;
    px(ctx, x, y, s, 1, "#94a3b8");
    px(ctx, x + s - 1, y, 1, s, "#94a3b8");
    px(ctx, x, y + s - 1, s, 1, "#94a3b8");
    ctx.globalAlpha = 1;
  });
}

/** 发怒：头顶怒气火花 */
function drawAngerMark(ctx: Ctx, t: number) {
  const flash = Math.floor(t / 120) % 2 === 0;
  const c = flash ? "#ff5c39" : "#ff5c8a"; // 橙红系警示
  const x = 23;
  const y = 3;
  px(ctx, x + 1, y, 1, 1, c);
  px(ctx, x + 3, y, 1, 1, c);
  px(ctx, x, y + 1, 1, 1, c);
  px(ctx, x + 2, y + 1, 1, 1, c);
  px(ctx, x + 4, y + 1, 1, 1, c);
  px(ctx, x + 1, y + 2, 1, 1, c);
  px(ctx, x + 3, y + 2, 1, 1, c);
}

// ════════════════════════ 九态拟人状态视觉签名（buddyStates 对齐）════════════════════════
// 每态独立粒子/光效（姿势与眼型在 drawBuddy 姿态解算分支），色板：冷青/极光紫/钛白/琥珀/橙红/冰青——禁荧光绿

/** calm 平静：极光紫缓晕环（呼吸明灭） */
function drawCalmAura(ctx: Ctx, t: number) {
  const a = 0.12 + 0.08 * (Math.sin(t / 700) * 0.5 + 0.5);
  ctx.globalAlpha = a;
  px(ctx, 5, 8, 22, 1, "#8b5cf6");
  px(ctx, 5, 27, 22, 1, "#8b5cf6");
  px(ctx, 4, 9, 1, 18, "#8b5cf6");
  px(ctx, 27, 9, 1, 18, "#8b5cf6");
  ctx.globalAlpha = 1;
}

/** joy 喜悦：琥珀/樱粉十字星火花上飘 */
function drawJoySparks(ctx: Ctx, t: number) {
  for (let i = 0; i < 4; i++) {
    const life = (t / 620 + i * 0.25) % 1;
    const x = 6 + i * 6 + Math.sin(life * Math.PI * 2) * 2;
    const y = 20 - life * 15;
    const c = i % 2 === 0 ? "#fbbf24" : "#ff7ad9";
    ctx.globalAlpha = 1 - life;
    px(ctx, x + 1, y, 1, 3, c);
    px(ctx, x, y + 1, 3, 1, c);
    ctx.globalAlpha = 1;
  }
}

/** focus 专注：钛白四角聚气括弧收束 */
function drawFocusCorners(ctx: Ctx, t: number) {
  const inset = Math.floor(t / 360) % 2 === 0 ? 0 : 1;
  const c = "#e8f4ff";
  const L = 2 + inset;
  const R = 29 - inset;
  const T = 5 + inset;
  const B = 29 - inset;
  // 四角 L 形括弧
  px(ctx, L, T, 4, 1, c); px(ctx, L, T, 1, 4, c);
  px(ctx, R - 3, T, 4, 1, c); px(ctx, R, T, 1, 4, c);
  px(ctx, L, B, 4, 1, c); px(ctx, L, B - 3, 1, 4, c);
  px(ctx, R - 3, B, 4, 1, c); px(ctx, R, B - 3, 1, 4, c);
}

/** alert 警觉：冷青雷达波纹外扩 */
function drawAlertPings(ctx: Ctx, t: number) {
  for (let i = 0; i < 3; i++) {
    const life = (t / 700 + i * 0.33) % 1;
    const r = 4 + life * 10;
    ctx.globalAlpha = (1 - life) * 0.8;
    // 像素圆环近似：上下左右四段
    px(ctx, 16 - r, 16 - r * 0.4, 1, 1, "#00f0ff");
    px(ctx, 15 + r, 16 - r * 0.4, 1, 1, "#00f0ff");
    px(ctx, 16 - r * 0.4, 16 - r, 1, 1, "#00f0ff");
    px(ctx, 16 - r * 0.4, 15 + r, 1, 1, "#00f0ff");
    px(ctx, 16 - r * 0.7, 16 - r * 0.7, 1, 1, "#38bdf8");
    px(ctx, 15 + r * 0.7, 16 - r * 0.7, 1, 1, "#38bdf8");
    ctx.globalAlpha = 1;
  }
}

/** rage 愤怒：双怒气符 + 环绕橙红火花（强化 angry 单符） */
function drawRageBurst(ctx: Ctx, t: number) {
  const flash = Math.floor(t / 100) % 2 === 0;
  const c = flash ? "#ff5c39" : "#ff5c8a";
  for (const x of [6, 23]) {
    const y = 2;
    px(ctx, x + 1, y, 1, 1, c);
    px(ctx, x + 3, y, 1, 1, c);
    px(ctx, x, y + 1, 1, 1, c);
    px(ctx, x + 2, y + 1, 1, 1, c);
    px(ctx, x + 4, y + 1, 1, 1, c);
    px(ctx, x + 1, y + 2, 1, 1, c);
    px(ctx, x + 3, y + 2, 1, 1, c);
  }
  const ph = Math.floor(t / 90) % 4;
  px(ctx, 3 + ph, 12, 1, 1, "#ff9f5a");
  px(ctx, 28 - ph, 20, 1, 1, "#ff9f5a");
}

/** fear 恐惧：钛白惊恐锯齿 + 冷汗滴 */
function drawFearJag(ctx: Ctx, t: number) {
  const flash = Math.floor(t / 140) % 2 === 0;
  const c = flash ? "#e8f4ff" : "#94a3b8";
  // 头顶惊恐锯齿
  px(ctx, 10, 2, 1, 2, c); px(ctx, 12, 1, 1, 3, c); px(ctx, 14, 2, 1, 2, c);
  // 冷汗滴（右颊滑落）
  const drip = Math.floor(t / 220) % 3;
  px(ctx, 26, 12 + drip * 2, 1, 2, "#38bdf8");
}

/** fatigue 疲惫：灰蓝泄气省略号 + 塌肩气线 */
function drawFatigueDots(ctx: Ctx, t: number) {
  const step = Math.floor(t / 500) % 4;
  for (let i = 0; i < 3; i++) {
    const on = i <= step;
    px(ctx, 25 + i * 2, 8 - i, 1, 1, on ? "#94a3b8" : "rgba(148,163,184,0.35)");
  }
  // 泄气斜线
  px(ctx, 2, 10, 1, 3, "rgba(148,163,184,0.5)");
  px(ctx, 29, 10, 1, 3, "rgba(148,163,184,0.5)");
}

/** sorrow 悲伤：头顶微型雨云落冷青雨 */
function drawSorrowRain(ctx: Ctx, t: number) {
  // 雨云（钛白灰）
  px(ctx, 11, 1, 10, 2, "#94a3b8");
  px(ctx, 13, 0, 6, 1, "#94a3b8");
  // 雨滴下落（3 滴循环）
  for (let i = 0; i < 3; i++) {
    const life = (t / 480 + i * 0.33) % 1;
    const x = 13 + i * 3;
    const y = 4 + life * 8;
    ctx.globalAlpha = 1 - life * 0.6;
    px(ctx, x, y, 1, 2, "#38bdf8");
    ctx.globalAlpha = 1;
  }
}

/** trance 恍惚：紫青钛白三点环绕轨道 */
function drawTranceOrbit(ctx: Ctx, t: number) {
  const colors = ["#8b5cf6", "#00f0ff", "#e8f4ff"];
  for (let i = 0; i < 3; i++) {
    const ang = t / 500 + (i * Math.PI * 2) / 3;
    const x = 16 + Math.cos(ang) * 11;
    const y = 17 + Math.sin(ang) * 6;
    px(ctx, x, y, 2, 2, colors[i]);
  }
}

/** 九态视觉签名分发（每态独立渲染分支） */
function drawBuddyStateFx(ctx: Ctx, mode: BuddyAnim, t: number) {
  if (mode === "calm") drawCalmAura(ctx, t);
  else if (mode === "joy") drawJoySparks(ctx, t);
  else if (mode === "focus") drawFocusCorners(ctx, t);
  else if (mode === "alert") drawAlertPings(ctx, t);
  else if (mode === "rage") drawRageBurst(ctx, t);
  else if (mode === "fear") drawFearJag(ctx, t);
  else if (mode === "fatigue") drawFatigueDots(ctx, t);
  else if (mode === "sorrow") drawSorrowRain(ctx, t);
  else if (mode === "trance") drawTranceOrbit(ctx, t);
}

const RARITY_COLOR: Record<Rarity, string> = {
  common: "#94a3b8", // 冷灰
  fine: "#00f0ff", // 全息冷青
  rare: "#8b5cf6", // 极光紫
  legendary: "#fbbf24", // 琥珀金
};

// ════════════════════════ 出场角色双身份呈现（工单：KERNELHUB / iCLOSER）════════════════════════
// KERNELHUB（默认）：现有灵机（信 XIN）外观保持不动；
// iCLOSER：金/琥珀色调换色 + 胸口徽记像素 + 徽记字样（面板名牌）。禁荧光绿。

/** iCLOSER 身份换色（批准色板：琥珀金主色 + 金高光） */
const ICLOSER_BODY_COLOR = "#fbbf24"; // 琥珀金
const ICLOSER_ACCENT_COLOR = "#ffe066"; // 金

/** 身份换色：iCLOSER 覆盖 body/accent，其余（物种/配饰/shiny）保持种子确定性 */
function icloserizeTraits(traits: BuddyTraits): BuddyTraits {
  return {
    ...traits,
    bodyColor: ICLOSER_BODY_COLOR,
    accentColor: ICLOSER_ACCENT_COLOR,
  };
}

/** iCLOSER 身份徽记：胸口琥珀菱形闪点（kernelhub 不绘制，现外观保持） */
function drawIdentityBadge(ctx: Ctx, identity: BuddyIdentity, t: number) {
  if (identity !== "icloser") return;
  const flash = Math.floor(t / 400) % 2 === 0;
  const c = flash ? "#ffe066" : "#fbbf24";
  px(ctx, 15, 18, 2, 1, c);
  px(ctx, 14, 19, 4, 1, c);
  px(ctx, 15, 20, 2, 1, c);
}

export function BuddyXin({
  seedInput,
  anim,
  onPoke,
  className = "",
}: BuddyXinProps) {
  const traits = useMemo(() => rollBuddy(seedInput), [seedInput]);
  /** 出场角色身份（READY 卡选定，全程生效）：iCLOSER 换色换徽，KERNELHUB 外观保持 */
  const identity = useSyncExternalStore(
    subscribeBuddyIdentity,
    getBuddyIdentity,
    getBuddyIdentity,
  );
  const viewTraits = useMemo(
    () => (identity === "icloser" ? icloserizeTraits(traits) : traits),
    [traits, identity],
  );
  const idMeta = BUDDY_IDENTITY_META[identity];
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mouseRef = useRef<Pt | null>(null);
  const animRef = useRef<BuddyAnim>(anim);
  /** 点击触发的本地姿势覆盖；null = 跟随 props.anim */
  const overrideRef = useRef<BuddyAnim | null>(null);
  const lastModeRef = useRef<BuddyAnim | null>(null);
  const modeStartRef = useRef(0);
  const clickCountRef = useRef(0);
  const timersRef = useRef<number[]>([]);

  useEffect(() => {
    animRef.current = anim;
  }, [anim]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  // ── 渲染循环 ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // 整数放大：CSS 192px，背衬按 dpr 整数倍加细
    const ds = Math.max(1, Math.min(2, Math.round(window.devicePixelRatio || 1)));
    canvas.width = GRID * SCALE * ds;
    canvas.height = GRID * SCALE * ds;
    canvas.style.width = `${GRID * SCALE}px`;
    canvas.style.height = `${GRID * SCALE}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;

    let raf = 0;
    const loop = () => {
      const t = performance.now();
      const mode: BuddyAnim = overrideRef.current ?? animRef.current;
      if (mode !== lastModeRef.current) {
        lastModeRef.current = mode;
        modeStartRef.current = t;
      }
      ctx.setTransform(ds * SCALE, 0, 0, ds * SCALE, 0, 0);
      ctx.clearRect(0, 0, GRID, GRID);
      drawBuddy(ctx, viewTraits, {
        t,
        mouse: mouseRef.current,
        mode,
        elapsed: t - modeStartRef.current,
      });
      if (mode === "pet") drawHearts(ctx, t);
      if (mode === "sleep") drawZzz(ctx, t);
      if (mode === "angry") drawAngerMark(ctx, t);
      drawBuddyStateFx(ctx, mode, t); // 九态拟人视觉签名（非九态时为空操作）
      drawIdentityBadge(ctx, identity, t); // iCLOSER 身份徽记（kernelhub 空操作）
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [viewTraits, identity]);

  // ── 瞳孔追踪：全局 mousemove ──
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0) return;
      mouseRef.current = {
        x: ((e.clientX - rect.left) / rect.width) * GRID,
        y: ((e.clientY - rect.top) / rect.height) * GRID,
      };
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, []);

  // ── 摸头互动：本地 pet 动画 + playCoin + props.onPoke ──
  const handlePoke = useCallback(() => {
    clickCountRef.current += 1;
    const timers = timersRef.current;

    // 800ms 内连续戳弄 → 发怒
    timers.push(
      window.setTimeout(() => {
        clickCountRef.current = 0;
      }, 800),
    );

    const spam = clickCountRef.current >= 3;
    overrideRef.current = spam ? "angry" : "pet";

    audioEngine.play("coin");
    if (spam) {
      audioEngine.play("alert");
      clickCountRef.current = 0;
    }
    onPoke();

    timers.push(
      window.setTimeout(() => {
        overrideRef.current = null;
      }, spam ? 1600 : 1200),
    );
  }, [onPoke]);

  return (
    <div className={`relative flex flex-col items-center gap-3 ${className}`}>
      <canvas
        ref={canvasRef}
        className="cursor-pointer select-none"
        style={{ imageRendering: "pixelated" }}
        onPointerDown={handlePoke}
        aria-label={
          identity === "icloser"
            ? "像素伴侣 iCloser 灵机，点击可摸头互动"
            : "像素伴侣信（XIN），点击可摸头互动"
        }
        role="img"
      />

      {/* ── 身份徽记字样（KERNELHUB=信 XIN / iCLOSER=琥珀金徽） ── */}
      <div
        className="px-border-in px-2 py-0.5 font-pixel text-[7px] tracking-[0.22em]"
        style={{ color: idMeta.accent }}
        data-identity={identity}
      >
        ◈ {idMeta.badge} // {identity === "icloser" ? "钱包基础设施" : "神枢中继"}
      </div>

      {/* ── 伴侣面板 ── */}
      <div className="w-full max-w-[280px]">
        <div className="px-border-in bg-[#151a28] px-2 py-1.5">
          <div className="flex items-center justify-between gap-2">
            <span
              className="font-pixel text-[8px] tracking-wider"
              style={{ color: identity === "icloser" ? "#fbbf24" : "#00f0ff" }}
            >
              {idMeta.badge} · {SPECIES_LABEL[traits.species]}
            </span>
            {traits.shiny ? (
              <span className="font-pixel text-[7px] text-[#fbbf24] animate-px-blink">
                ★ SHINY
              </span>
            ) : null}
          </div>
          <div className="mt-1 break-all font-pixel text-[6px] text-[#94a3b8]">
            SEED {traits.seedLabel}
          </div>
        </div>

        {/* 五维属性（Soul 灵魂层）— 色板：冷青/极光紫/钛白/冷灰，VAULT 数值用琥珀金 */}
        <div className="mt-2 px-border-in bg-[#0f111a] px-2 py-1.5">
          <div className="mb-1.5 font-pixel text-[7px] tracking-wider text-[#8b5cf6]">
            SOUL MATRIX // 五维属性
          </div>
          <div className="space-y-1">
            {STAT_META.map((s) => (
              <div key={s.key}>
                <div className="flex items-center gap-2">
                  <span className="w-12 font-pixel text-[7px] text-[#f1f5f9]">
                    {s.label}
                  </span>
                  <div className="px-border-in h-2 flex-1 bg-[#0f111a]">
                    <div
                      className="h-full transition-all duration-500"
                      style={{
                        width: `${traits.stats[s.key]}%`,
                        background: "linear-gradient(90deg, #00f0ff, #8b5cf6)",
                      }}
                    />
                  </div>
                  <span
                    className="w-7 text-right font-pixel text-[7px] tabular-nums"
                    style={{ color: s.key === "VAULT" ? "#fbbf24" : "#f1f5f9" }}
                  >
                    {traits.stats[s.key]}
                  </span>
                </div>
                <div className="pl-1 font-zpix text-[8px] leading-tight text-[#94a3b8]">
                  {s.note}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 配饰稀有度 */}
        <div className="mt-2 flex flex-wrap gap-1">
          {traits.accessories.map((a) => (
            <span
              key={a}
              className="px-border-in px-1.5 py-0.5 font-zpix text-[9px]"
              style={{ color: RARITY_COLOR[ACCESSORY_META[a].rarity] }}
            >
              {ACCESSORY_LABEL[a]}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default BuddyXin;
